import { useState, useEffect, useRef } from 'react';
import { api, Conversation, CoachMessage } from '../../lib/api';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const phaseNames: Record<number, string> = {
  1: 'Business Profile', 2: 'Business Intel', 3: 'Brand Guide', 4: 'ICP',
  5: 'Assets', 6: 'Website', 7: 'Blog', 8: 'Social', 9: 'Messaging', 10: 'Activation',
};

export default function AdminCoachInboxPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load conversations
  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getConversations();
        setConversations(data.conversations);
        if (data.conversations.length > 0 && !selectedId) {
          setSelectedId(data.conversations[0].client_id);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  // Load selected conversation
  useEffect(() => {
    if (!selectedId) return;
    const loadConvo = async () => {
      try {
        const data = await api.getConversation(selectedId);
        setMessages(data.messages);
        setClientInfo(data.client);
        await api.markConversationRead(selectedId);
        // Update unread count in sidebar
        setConversations(prev => prev.map(c =>
          c.client_id === selectedId ? { ...c, unread_count: 0 } : c
        ));
      } catch (e) { console.error(e); }
    };
    loadConvo();
    const interval = setInterval(loadConvo, 10000);
    return () => clearInterval(interval);
  }, [selectedId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!reply.trim() || !selectedId || sending) return;
    setSending(true);
    try {
      const result = await api.replyToConversation(selectedId, reply.trim());
      setMessages(prev => [...prev, result.message]);
      setReply('');
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-charcoal-800">Coach Inbox</h1>

      <div className="flex gap-4 h-[calc(100vh-180px)]">
        {/* Conversation List */}
        <div className="w-80 bg-white rounded-xl border border-gray-200 flex flex-col flex-shrink-0 overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-500">{conversations.length} conversations</p>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <ChatBubbleLeftRightIcon className="h-10 w-10 mx-auto mb-2" />
                <p className="text-sm">No conversations yet</p>
              </div>
            ) : conversations.map(c => (
              <button
                key={c.client_id}
                onClick={() => setSelectedId(c.client_id)}
                className={`w-full text-left p-3 transition-colors ${
                  selectedId === c.client_id ? 'bg-copper-50 border-l-2 border-copper-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-charcoal-800 truncate">{c.business_name}</span>
                  {c.unread_count > 0 && (
                    <span className="bg-copper-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full ml-2">
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mb-1">Phase {c.current_phase} — {phaseNames[c.current_phase]}</p>
                {c.last_message && (
                  <p className="text-xs text-gray-500 truncate">
                    {c.last_message.sender_type === 'coach' ? 'You: ' : ''}{c.last_message.message}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <p>Select a conversation</p>
            </div>
          ) : (
            <>
              {/* Client context header */}
              {clientInfo && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-charcoal-800">{clientInfo.business_name}</h3>
                      <p className="text-xs text-gray-500">{clientInfo.email} &middot; Phase {clientInfo.current_phase} — {phaseNames[clientInfo.current_phase]}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      clientInfo.status === 'onboarding' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {clientInfo.status}
                    </span>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_type === 'coach' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                      m.sender_type === 'coach'
                        ? 'bg-teal-600 text-white rounded-br-md'
                        : 'bg-gray-100 text-charcoal-800 rounded-bl-md'
                    }`}>
                      {m.page_url && m.sender_type === 'client' && (
                        <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                          <MapPinIcon className="h-3 w-3" />
                          {m.page_url}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                      <p className={`text-xs mt-1 ${m.sender_type === 'coach' ? 'text-teal-200' : 'text-gray-400'}`}>
                        {m.created_at ? formatTime(m.created_at) : ''}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="p-3 border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    placeholder="Type your reply..."
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || !reply.trim()}
                    className="px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
